import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Catégories de motifs d'annulation
const CANCELLATION_REASONS = {
  'client_unreachable': 'Client injoignable',
  'wrong_number': 'Mauvais numéro',
  'changed_mind': 'Client a changé d\'avis',
  'price_issue': 'Problème de prix',
  'delivery_issue': 'Problème de livraison',
  'out_of_stock': 'Rupture de stock',
  'duplicate_order': 'Commande en double',
  'other': 'Autre raison'
};

const CancellationAnalysis = ({ orders }) => {
  const analysisData = useMemo(() => {
    // Filtrer les commandes annulées
    const cancelledOrders = orders.filter(order => order.status === 'cancelled');
    
    // Statistiques par motif
    const reasonStats = Object.keys(CANCELLATION_REASONS).reduce((acc, reason) => {
      acc[reason] = {
        count: 0,
        revenue: 0,
        orders: []
      };
      return acc;
    }, {});

    // Statistiques par période
    const timeStats = {
      morning: { count: 0, hours: '6h-12h' },
      afternoon: { count: 0, hours: '12h-18h' },
      evening: { count: 0, hours: '18h-00h' },
      night: { count: 0, hours: '00h-6h' }
    };

    // Analyser chaque commande annulée
    cancelledOrders.forEach(order => {
      const reason = order.cancellationReason || 'other';
      const orderDate = order.createdAt?.seconds 
        ? new Date(order.createdAt.seconds * 1000) 
        : new Date(order.createdAt);
      
      // Statistiques par motif
      reasonStats[reason].count++;
      reasonStats[reason].revenue += order.totalPrice || 0;
      reasonStats[reason].orders.push(order);

      // Statistiques par période
      const hour = orderDate.getHours();
      if (hour >= 6 && hour < 12) timeStats.morning.count++;
      else if (hour >= 12 && hour < 18) timeStats.afternoon.count++;
      else if (hour >= 18) timeStats.evening.count++;
      else timeStats.night.count++;
    });

    // Calculer les pourcentages et le total
    const totalCancelled = cancelledOrders.length;
    const totalOrders = orders.length;
    const cancellationRate = totalOrders ? (totalCancelled / totalOrders * 100).toFixed(1) : 0;
    const lostRevenue = cancelledOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);

    return {
      reasonStats,
      timeStats,
      totalCancelled,
      totalOrders,
      cancellationRate,
      lostRevenue
    };
  }, [orders]);

  // Données pour le graphique circulaire des motifs
  const reasonChartData = {
    labels: Object.values(CANCELLATION_REASONS),
    datasets: [
      {
        data: Object.keys(CANCELLATION_REASONS).map(
          reason => analysisData.reasonStats[reason].count
        ),
        backgroundColor: [
          'rgb(59, 130, 246)',   // Bleu
          'rgb(16, 185, 129)',   // Vert
          'rgb(245, 158, 11)',   // Orange
          'rgb(239, 68, 68)',    // Rouge
          'rgb(139, 92, 246)',   // Violet
          'rgb(236, 72, 153)',   // Rose
          'rgb(14, 165, 233)',   // Bleu clair
          'rgb(168, 162, 158)'   // Gris
        ]
      }
    ]
  };

  // Données pour le graphique en barres des périodes
  const timeChartData = {
    labels: Object.values(analysisData.timeStats).map(stat => stat.hours),
    datasets: [
      {
        label: 'Annulations par période',
        data: Object.values(analysisData.timeStats).map(stat => stat.count),
        backgroundColor: 'rgb(59, 130, 246)'
      }
    ]
  };

  // Options des graphiques
  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const reason = Object.keys(CANCELLATION_REASONS)[context.dataIndex];
            const stats = analysisData.reasonStats[reason];
            const percentage = ((stats.count / analysisData.totalCancelled) * 100).toFixed(1);
            return [
              `${context.label}: ${stats.count} (${percentage}%)`,
              `CA perdu: ${stats.revenue.toLocaleString('fr-DZ')} DA`
            ];
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const percentage = ((context.raw / analysisData.totalCancelled) * 100).toFixed(1);
            return `${context.raw} annulations (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  // Trouver le motif le plus fréquent
  const topReason = Object.entries(analysisData.reasonStats)
    .reduce((max, [reason, stats]) => 
      stats.count > (max.count || 0) ? { reason, count: stats.count } : max
    , {});

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Analyse des annulations</h3>

        {/* Résumé */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-red-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-red-900 mb-1">Taux d'annulation</h4>
            <p className="text-2xl font-semibold text-red-900">{analysisData.cancellationRate}%</p>
            <p className="text-sm text-red-700 mt-1">
              {analysisData.totalCancelled} sur {analysisData.totalOrders} commandes
            </p>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-orange-900 mb-1">Chiffre d'affaires perdu</h4>
            <p className="text-2xl font-semibold text-orange-900">
              {analysisData.lostRevenue.toLocaleString('fr-DZ')} DA
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-1">Motif principal</h4>
            <p className="text-2xl font-semibold text-blue-900">
              {CANCELLATION_REASONS[topReason.reason]}
            </p>
            <p className="text-sm text-blue-700 mt-1">
              {((topReason.count / analysisData.totalCancelled) * 100).toFixed(1)}% des annulations
            </p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-purple-900 mb-1">Coût moyen</h4>
            <p className="text-2xl font-semibold text-purple-900">
              {analysisData.totalCancelled 
                ? Math.round(analysisData.lostRevenue / analysisData.totalCancelled).toLocaleString('fr-DZ')
                : 0} DA
            </p>
            <p className="text-sm text-purple-700 mt-1">par annulation</p>
          </div>
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Répartition par motif</h4>
            <div className="relative" style={{ height: '300px' }}>
              <Pie data={reasonChartData} options={pieOptions} />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Répartition par période</h4>
            <div className="relative" style={{ height: '300px' }}>
              <Bar data={timeChartData} options={barOptions} />
            </div>
          </div>
        </div>

        {/* Recommandations */}
        <div className="mt-8">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recommandations</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            {topReason.reason === 'client_unreachable' && (
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Envisager d'envoyer des SMS de confirmation automatiques
              </li>
            )}
            {topReason.reason === 'wrong_number' && (
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Mettre en place une validation du numéro de téléphone
              </li>
            )}
            {topReason.reason === 'price_issue' && (
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Afficher les frais de livraison plus tôt dans le processus
              </li>
            )}
            {topReason.reason === 'delivery_issue' && (
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Revoir les zones de livraison et les délais annoncés
              </li>
            )}
            <li className="flex items-start">
              <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Contacter les clients rapidement pendant les heures de pointe ({
                Object.entries(analysisData.timeStats)
                  .reduce((max, [period, stats]) => 
                    stats.count > (max.count || 0) ? { period, count: stats.count, hours: stats.hours } : max
                  , {}).hours
              })
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CancellationAnalysis;
